import styled, { keyframes } from "styled-components";

import { md3CardRadius } from "../../shared/styles/md3Components";
import {
  actionListSubtitle,
  actionListTitle,
  fontSizes,
  lineHeights,
  sectionTitle,
} from "../../shared/styles/typography";

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const SectionHeader = styled.div`
  padding: 0 0.2rem;
`;

export const SectionTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 0.2rem;
`;

interface SectionIconProps {
  $color: string;
}

export const SectionIcon = styled.span<SectionIconProps>`
  width: 3.6rem;
  height: 3.6rem;
  border-radius: 1.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: color-mix(
    in srgb,
    ${(p) => p.$color} 16%,
    ${({ theme }) => theme.colors.surface}
  );
  border: 1px solid color-mix(in srgb, ${(p) => p.$color} 35%, transparent);
  box-shadow: ${({ theme }) => theme.shadows.md3Level1};
`;

export const SectionTitle = styled.h2`
  ${sectionTitle}
  margin: 0;
`;

export const SectionSubtitle = styled.p`
  ${actionListSubtitle}
  margin: 0.4rem 0 0;
  padding-left: 0.2rem;
`;

export const ModuleGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.8rem;

  @media (min-width: 540px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 1.2rem;
  }
`;

interface ModuleCardProps {
  $cardColor: string;
  $delayIndex?: number;
}

export const ModuleCardIcon = styled.div<ModuleCardProps>`
  flex-shrink: 0;
  width: 4.8rem;
  height: 4.8rem;
  border-radius: 1.4rem;
  background: color-mix(
    in srgb,
    ${(p) => p.$cardColor} 14%,
    ${({ theme }) => theme.colors.surface}
  );
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.25s ease, transform 0.25s ease;
  border: 1px solid color-mix(in srgb, ${(p) => p.$cardColor} 22%, transparent);

  svg,
  svg path {
    transition: color 0.25s ease, fill 0.25s ease;
  }
`;

export const ModuleCardButton = styled.button<ModuleCardProps>`
  display: flex;
  align-items: center;
  gap: 1.2rem;
  width: 100%;
  padding: 1.6rem 1.8rem;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => md3CardRadius(theme)}px;
  cursor: pointer;
  text-align: left;
  transition: transform 0.25s cubic-bezier(0.25, 0.8, 0.25, 1),
    box-shadow 0.25s ease, border-color 0.25s ease, background 0.25s ease;
  box-shadow: ${({ theme }) => theme.shadows.md3Level1};
  -webkit-tap-highlight-color: transparent;

  opacity: 0;
  animation: ${fadeUp} 0.55s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: ${(p) => (p.$delayIndex ? p.$delayIndex * 0.08 : 0)}s;

  &:hover,
  &:focus-visible {
    transform: translateY(-4px);
    box-shadow: 0 12px 28px
        color-mix(in srgb, ${(p) => p.$cardColor} 22%, transparent),
      ${({ theme }) => theme.shadows.md3Level2};
    border-color: color-mix(
      in srgb,
      ${(p) => p.$cardColor} 55%,
      ${({ theme }) => theme.colors.border}
    );
    outline: none;
    background: ${({ theme }) => theme.colors.surfaceSecondary};

    ${ModuleCardIcon} {
      background: ${(p) => p.$cardColor};
      border-color: transparent;

      svg,
      svg path {
        color: ${({ theme }) => theme.colors.white} !important;
        fill: ${({ theme }) => theme.colors.white} !important;
      }
    }
  }

  &:active {
    transform: scale(0.98);
  }

  @media (min-width: 900px) {
    flex-direction: column;
    align-items: flex-start;
    padding: 2.2rem 2rem;
    gap: 1.4rem;
    min-height: 16rem;
  }
`;

export const ModuleCardBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  overflow: hidden;
  min-width: 0;
`;

export const ModuleCardTitle = styled.strong`
  ${actionListTitle}
  color: ${({ theme }) => theme.colors.textTitle};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  @media (min-width: 900px) {
    white-space: normal;
  }
`;

export const ModuleCardDesc = styled.p`
  font-size: ${fontSizes.sm}px;
  line-height: ${lineHeights.sm}px;
  font-weight: ${({ theme }) => theme.fontWeight.body};
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;

  @media (min-width: 900px) {
    white-space: normal;
    overflow: visible;
  }
`;

export const ModuleCardArrow = styled.span<ModuleCardProps>`
  flex-shrink: 0;
  font-size: 2rem;
  line-height: 1;
  color: ${(p) => p.$cardColor};
  opacity: 0.55;
  font-weight: 300;

  @media (min-width: 900px) {
    display: none;
  }
`;
